"use client";

import { useRef, type ReactNode } from "react";
import {
  DARK_BAND,
  GradText,
  SectionHead,
  SectionShell,
  useBandRowAlign,
  useWholeRowBlocks,
} from "./kit";
import { NOT_REDUCED, gsap, useEnterStagger, useGSAP } from "./kit/motion";

/** One in-place swap point: both variants stacked in the same grid cell (the
 *  cell reserves the wider one, so swapping never moves layout), the wrapper
 *  clips the roll. Default/static state shows the BRANDED variant (SSR,
 *  mobile, reduced motion); the enhanced timeline flips to generic first. */
function Swap({ id, generic, branded }: { id: string; generic: ReactNode; branded: ReactNode }) {
  return (
    <span className="grid min-w-0 overflow-hidden">
      <span data-swap={`g-${id}`} className="min-w-0 opacity-0 [grid-area:1/1] justify-self-start">
        {generic}
      </span>
      <span data-swap={`b-${id}`} className="min-w-0 [grid-area:1/1] justify-self-start">
        {branded}
      </span>
    </span>
  );
}

const SWAP_IDS = ["pill", "source", "badge"] as const;

/** The share-card artifact — ONE card that rebrands itself in place. The
 *  parts the link domain does not control (sender, message, og image, title)
 *  never move; the three parts it does control — URL pill, source row, badge —
 *  roll from generic to branded in a staggered loop, so the eye lands
 *  exactly on what a custom domain changes. */
function ShareCard() {
  const cardRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(`${NOT_REDUCED} and (min-width: 768px)`, () => {
        const q = gsap.utils.selector(cardRef);
        const g = (id: string) => q(`[data-swap="g-${id}"]`);
        const b = (id: string) => q(`[data-swap="b-${id}"]`);

        // Enhanced start state: generic visible, branded parked below, the
        // og image desaturated (its gray overlay shown).
        SWAP_IDS.forEach((id) => {
          gsap.set(g(id), { autoAlpha: 1, yPercent: 0 });
          gsap.set(b(id), { autoAlpha: 0, yPercent: 110 });
        });
        const gray = q("[data-preview-gray]");
        gsap.set(gray, { autoAlpha: 1 });

        // generic hold → staggered roll to branded (badge pops on arrival) →
        // branded hold → quick roll back → repeat. One clock, no effects.
        // immediateRender:false everywhere: a later fromTo would otherwise
        // render its hidden "from" state at build time and blank the card's
        // generic state during the very first hold.
        const tl = gsap.timeline({
          paused: true,
          repeat: -1, // infinite → __ffwd skips it by design
          defaults: { ease: "power3.inOut", immediateRender: false },
        });
        tl.addLabel("brand", 1.6);
        SWAP_IDS.forEach((id, i) => {
          tl.to(g(id), { yPercent: -110, autoAlpha: 0, duration: 0.45 }, `brand+=${i * 0.16}`)
            .fromTo(
              b(id),
              { yPercent: 110, autoAlpha: 0 },
              { yPercent: 0, autoAlpha: 1, duration: 0.45 },
              `brand+=${i * 0.16 + 0.09}`,
            );
        });
        tl.fromTo(
          b("badge"),
          { scale: 1 },
          { scale: 1.14, duration: 0.16, ease: "power2.out", yoyo: true, repeat: 1 },
          "brand+=0.95",
        );
        // The og image colorizes as the rebrand cascade runs.
        tl.to(gray, { autoAlpha: 0, duration: 0.7, ease: "power2.inOut" }, "brand+=0.25");
        tl.addLabel("revert", "+=3");
        tl.to(gray, { autoAlpha: 1, duration: 0.5 }, "revert+=0.1");
        SWAP_IDS.forEach((id, i) => {
          tl.to(b(id), { yPercent: 110, autoAlpha: 0, duration: 0.35 }, `revert+=${i * 0.09}`)
            .fromTo(
              g(id),
              { yPercent: -110, autoAlpha: 0 },
              { yPercent: 0, autoAlpha: 1, duration: 0.35 },
              `revert+=${i * 0.09 + 0.07}`,
            );
        });

        // Runs only while on screen; pauses (not kills) when scrolled away.
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => (e.isIntersecting ? tl.play() : tl.pause()));
          },
          { rootMargin: "0px 0px -10% 0px" },
        );
        if (cardRef.current) io.observe(cardRef.current);
        return () => {
          io.disconnect();
          tl.kill();
        };
      });
    },
    { scope: cardRef },
  );

  return (
    <div ref={cardRef} className="w-[min(520px,100%)] rounded-2xl border border-white/10 bg-[#1b1b26] p-4">
      {/* sender row */}
      <div className="flex items-center gap-2.5">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-white/12 text-[11px] font-bold text-white/75">
          A
        </span>
        <span className="text-[13px] font-semibold text-white/85">Alex</span>
        <span className="font-jetbrains text-[11px] text-white/40">12:03</span>
      </div>
      <p className="mt-2 text-[13.5px] text-white/75">check this out 👀</p>

      {/* the link pill — swap point 1 */}
      <div className="mt-2.5">
        <Swap
          id="pill"
          generic={
            <span className="inline-flex max-w-full items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3.5 py-1.5 font-jetbrains text-[12.5px] text-white/60">
              <span className="h-2 w-2 shrink-0 rounded-sm bg-white/20" />
              <span className="truncate">rii.link/x3f9</span>
            </span>
          }
          branded={
            <span className="inline-flex max-w-full items-center gap-2 rounded-full border border-brand-600/50 bg-brand-600/[0.14] px-3.5 py-1.5 font-jetbrains text-[12.5px] text-brand-500">
              <span className="h-2 w-2 shrink-0 rounded-sm bg-brand-600" />
              <span className="truncate">links.yourbrand.com/launch</span>
            </span>
          }
        />
      </div>

      {/* og preview card — same image in both states (it comes from the
          TARGET page, not the link domain); the generic state shows it
          desaturated, and it colorizes as the card rebrands — the section's
          attention moment. */}
      <div className="mt-3 overflow-hidden rounded-xl border border-white/10">
        <div className="relative h-[110px] bg-[linear-gradient(120deg,#d70e62,#800938_55%,#2f6be0)]">
          <div
            data-preview-gray
            aria-hidden
            className="absolute inset-0 bg-[linear-gradient(120deg,#d70e62,#800938_55%,#2f6be0)] opacity-0"
            style={{ filter: "grayscale(1) brightness(0.55)" }}
          />
        </div>
        <div className="bg-[#14141c] px-4 py-3">
          <div className="flex items-center gap-2">
            {/* source row — swap point 2 */}
            <Swap
              id="source"
              generic={
                <span className="flex items-center gap-2">
                  <span className="h-3.5 w-3.5 shrink-0 rounded-[4px] bg-white/20" />
                  <span className="truncate font-jetbrains text-[11px] uppercase tracking-[0.08em] text-white/45">
                    rii.link
                  </span>
                </span>
              }
              branded={
                <span className="flex items-center gap-2">
                  <span className="h-3.5 w-3.5 shrink-0 rounded-[4px] bg-brand-600" />
                  <span className="truncate font-jetbrains text-[11px] uppercase tracking-[0.08em] text-white/45">
                    links.yourbrand.com
                  </span>
                </span>
              }
            />
            {/* Recognition badge — swap point 3 (no CTR figures: rii.link
                doesn't measure click-through rate, and none are sourced). */}
            <span className="ml-auto shrink-0">
              <Swap
                id="badge"
                generic={
                  <span className="inline-block rounded-full bg-white/[0.08] px-2 py-[2px] font-jetbrains text-[10.5px] font-semibold text-white/45">
                    unknown domain
                  </span>
                }
                branded={
                  <span className="inline-block rounded-full bg-data-green/[0.16] px-2 py-[2px] font-jetbrains text-[10.5px] font-semibold text-data-green">
                    your brand ✓
                  </span>
                }
              />
            </span>
          </div>
          <div className="mt-1.5 truncate text-[14px] font-semibold text-white">
            Launch week — 40% off · yourbrand
          </div>
        </div>
      </div>
    </div>
  );
}

/** Stylized QR module matrix (deterministic, decorative). */
const QR = [
  "1111101011",
  "1000101110",
  "1011101001",
  "1011100110",
  "1000101011",
  "1111100010",
  "0000001101",
  "1110111010",
  "0101010111",
  "1101001100",
];

/** Printed flyer/sticker with a QR — the artifact of the print vignette. */
function Flyer({ branded }: { branded: boolean }) {
  return (
    <div className="w-full max-w-[240px] rounded-xl bg-white p-4 shadow-ref">
      <div className="mx-auto grid w-fit grid-cols-10 gap-[2px]">
        {QR.flatMap((row, y) =>
          row.split("").map((c, x) => (
            <span
              key={`${y}-${x}`}
              className={`h-[7px] w-[7px] rounded-[1.5px] ${
                c === "1" ? "bg-[#14141c]" : "bg-transparent"
              }`}
            />
          )),
        )}
      </div>
      <div
        className={`mt-3 truncate text-center font-jetbrains text-[11.5px] ${
          branded ? "text-brand-600" : "text-slate-500"
        }`}
      >
        {branded ? "links.yourbrand.com/menu" : "rii.link/x3f9"}
      </div>
      <div
        className={`mt-2 flex items-center justify-center gap-1.5 rounded-md px-2 py-1 font-jetbrains text-[10.5px] font-semibold ${
          branded ? "bg-data-green/[0.12] text-data-green" : "bg-brand-600/[0.08] text-brand-600"
        }`}
      >
        {branded ? (
          <>
            <span className="relative h-[6px] w-[6px] rounded-full bg-data-green">
              <span className="absolute -inset-[2px] animate-status-pulse rounded-full" />
            </span>
            always resolves — yours
          </>
        ) : (
          <>✗ provider gone → 404</>
        )}
      </div>
    </div>
  );
}

/** Search-result mockup — the reputation vignette's artifact. */
function SerpCard() {
  return (
    <div className="w-full max-w-[460px] min-w-0 rounded-xl bg-white p-5 shadow-ref">
      <div className="flex items-center gap-2 rounded-full border border-line bg-surface-2 px-3.5 py-1.5 text-[12.5px] text-muted">
        <span aria-hidden>⌕</span> yourbrand launch
      </div>
      <div className="mt-4">
        <div className="flex items-center gap-2">
          <span className="h-4 w-4 rounded-[4px] bg-brand-600" />
          <span className="font-jetbrains text-[11.5px] text-data-green">
            links.yourbrand.com/launch
          </span>
        </div>
        <div className="mt-1 text-[15.5px] font-semibold text-data-blue">
          Launch week — 40% off · yourbrand
        </div>
        <div className="mt-0.5 text-[12.5px] leading-[1.5] text-muted">
          Everything in the launch collection — routed, tracked and served from your own domain.
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {["Shop", "Menu", "Launch"].map((s) => (
            <span
              key={s}
              className="rounded-full border border-line px-2.5 py-[3px] text-[11px] text-muted"
            >
              {s}
            </span>
          ))}
        </div>
      </div>
      <div className="mt-4 border-t border-line pt-3 opacity-50">
        <div className="font-jetbrains text-[11.5px] text-slate-400 line-through">
          rii.link/x3f9
        </div>
        <div className="text-[12px] text-muted">No history. Reputation shared with strangers&apos; links.</div>
      </div>
    </div>
  );
}

const VIGNETTES = [
  {
    id: "print",
    num: "01",
    kicker: "Print & QR",
    title: "Printed once. Working forever.",
    desc: "A QR on ten thousand flyers can't be reprinted — on your own domain it never has to be. Leave rii tomorrow, point the domain at your own server, and every printed code still reaches you. On a shared shortener domain, the day the provider dies, so does your print run.",
    flip: false,
    sr: "Example: a printed flyer QR pointing at a generic shortener link stops working if the provider disappears; the same QR on links.yourbrand.com can still reach you, because you can point your own domain anywhere.",
    visual: (
      <div className="flex flex-wrap items-stretch justify-center gap-5">
        <Flyer branded={false} />
        <Flyer branded />
      </div>
    ),
  },
  {
    id: "trust",
    num: "02",
    kicker: "Trust",
    title: "People click what they recognize.",
    desc: "Same share, same preview — watch the card: only the domain people see before they click changes. A name your audience already knows converts; unknown shortener domains get skipped, filtered, or flagged.",
    flip: true,
    sr: "Example: the identical share — the only difference is the link domain. As rii.link/x3f9 it shows an unknown shortener domain; as links.yourbrand.com/launch people see a name they already know.",
    visual: (
      <div className="flex min-w-0 flex-col items-center gap-3">
        <ShareCard />
        {/* Static (mobile/reduced-motion) fallback shows the branded state —
            this caption keeps the generic reference visible there. */}
        <div className="font-jetbrains text-[11.5px] text-white/45 md:hidden">
          before: rii.link/x3f9 · unknown domain
        </div>
      </div>
    ),
  },
  {
    id: "reputation",
    num: "03",
    kicker: "Reputation",
    title: "Every click builds your domain. Not ours.",
    desc: "Search engines and mail filters score domains by their history. On your own domain that history compounds for you — rankings, previews, deliverability. On a shared domain you inherit everyone else's.",
    flip: false,
    tall: true,
    sr: "Example: a search result for links.yourbrand.com/launch with its own sitelinks and history, while the generic rii.link short link carries no history of its own.",
    visual: (
      <div className="flex justify-center">
        <SerpCard />
      </div>
    ),
  },
];

/**
 * Why your own domain wins — three concrete generic-vs-custom examples
 * (print/QR independence, share trust, search reputation), all visible
 * without interaction. Living details: the status pulse on the branded QR
 * and the trust card's in-place rebrand loop (ShareCard). Blocks are
 * row-aligned with guaranteed free rows by useBandRowAlign.
 */
export default function DomainWinsSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  useEnterStagger(rootRef, "[data-win-visual]", { y: 28, stagger: 0.1 });
  // Whole-row sizing first, then row alignment (the aligner must measure
  // final block heights); together they make every block start AND end on a
  // row line, so each vignette gap is exactly one visible free row.
  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);

  // md:pb-px — the aligner's one-cell margin after the last block IS the
  // band's single free bottom row (the default padding tail added a second);
  // the 1px keeps that margin from collapsing out through a padding-less
  // section edge.
  return (
    <SectionShell
      id="ref-domain-wins"
      band="dark"
      decoAvoid
      className="max-md:bg-[#14141c] md:pb-px"
    >
      <SectionHead
        center
        tone="light"
        gridFill={DARK_BAND}
        eyebrow="Why your own domain"
        title={
          <>
            Generic works. <GradText>Yours wins.</GradText>
          </>
        }
        lead="Three places a branded domain quietly outperforms any shared shortener — in print, in feeds, and on Google."
      />

      <div ref={rootRef} className="flex flex-col gap-[calc(var(--grid-cell,72px))] md:gap-0">
        {VIGNETTES.map((v) => (
          <div
            key={v.id}
            data-grid-block
            data-grid-fill={DARK_BAND}
            data-tall={"tall" in v && v.tall ? "" : undefined}
            className="grid items-center gap-10 md:grid-cols-[1fr_1.2fr]"
          >
            <div className={`min-w-0 ${v.flip ? "md:order-2" : ""}`}>
              <div className="font-jetbrains text-[13px] tracking-[0.08em] text-white/45">
                {v.num} · {v.kicker.toUpperCase()}
              </div>
              <h3 className="mt-3 text-[clamp(22px,2.4vw,30px)] font-bold tracking-[-0.02em] text-white">
                {v.title}
              </h3>
              <p className="mt-3 max-w-[460px] text-[15.5px] leading-[1.65] text-white/70">
                {v.desc}
              </p>
              <p className="sr-only">{v.sr}</p>
            </div>
            {/* Mock UI (QR flyers, share cards, SERP) — the sr-only summary
                above carries the same information for screen readers. */}
            <div
              data-win-visual
              aria-hidden
              className={`min-w-0 ${v.flip ? "md:order-1" : ""}`}
            >
              {v.visual}
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}
